import { memo, useMemo, useState } from "react";

import type { AxiosError } from "axios";
import { isEmpty } from "lodash-es";

import { Modal, ModalFooter, ModalHeader } from "@parspec/pixel";

import type { BulkImportCompany, ImportFileResponse, ValidateFileResponse } from "../Company/queries/apiTypes";
import type { BulkImportContact } from "../Contacts/queries/apiTypes";
import type { BulkSelectedRecord, ConflictPair, SummaryData } from "./types";

import ResolveConflictsContent from "./ResolveConflicts/ResolveConflictsContent";
import ImportSummaryContent from "./ImportSummaryContent";
import { getValueMapById } from "./utils";
import { useBulkUploadMutation } from "../Company/queries";
import { CRM_ENTITY_TYPE } from "./constant";

interface ResolutionModalProps {
    initialData: ValidateFileResponse;
    onClose: () => void;
    onUploadSuccess: (response: ImportFileResponse) => void;
};

const SCREEN_TYPE = {
    CONFLICT: "CONFLICT",
    SUMMARY: "SUMMARY",
} as const;

type ResolutionScreenData =
    | { type: typeof SCREEN_TYPE.CONFLICT; data: ValidateFileResponse }
    | { type: typeof SCREEN_TYPE.SUMMARY; data: SummaryData };

const getInitialScreenState = (initialData: ValidateFileResponse): ResolutionScreenData => {
    const { update_companies, update_contacts } = initialData;
    const isCompanyConflictsEmpty = isEmpty(update_companies.companies) || isEmpty(update_companies.conflicts);
    const isContactConflictsEmpty = isEmpty(update_contacts.contacts) || isEmpty(update_contacts.conflicts);

    if (isCompanyConflictsEmpty && isContactConflictsEmpty) {
        // No conflicts to resolve directly move to Summary screen
        // The only diff with initialData is to pass the array of update_companies and contacts instead of {}
        return {
            type: SCREEN_TYPE.SUMMARY,
            data: {
                ...initialData,
                // No conflicts, only duplicate items will be updated, if there are any
                update_companies: update_companies.duplicates,
                update_contacts: update_contacts.duplicates,
            }
        };
    }

    return { type: SCREEN_TYPE.CONFLICT, data: initialData }
}

const ResolutionModal = memo(function ResolutionModal(props: ResolutionModalProps) {
    const { initialData, onClose, onUploadSuccess } = props;

    const { mutateAsync: bulkImport, isLoading: isUploading, error, isError } = useBulkUploadMutation();
    const axiosError = isError ? (error as AxiosError<{ message?: string }>) : null;

    const [screenData, setScreenData] = useState<ResolutionScreenData>(
        () => getInitialScreenState(initialData)
    );

    const isConflictScreen = screenData.type === SCREEN_TYPE.CONFLICT;

    const onConfirmImport = async () => {
        if (isConflictScreen) return;
        const { new_companies, update_companies, new_contacts, update_contacts } = screenData.data;

        try {
            const response = await bulkImport({
                new_companies,
                update_companies,
                new_contacts,
                update_contacts,
            });
            onUploadSuccess(response.data);
        } catch (err) { }
    }

    const handleNextStepAfterConflictResolve = (selectedRecords: BulkSelectedRecord[]) => {
        setScreenData((prev) => {
            // type will never by summary here, but TS doesn't know, so adding this check here.
            if (prev.type === SCREEN_TYPE.SUMMARY) return prev;

            const updatedItemsObj = selectedRecords
                .reduce<{ update_companies: Array<BulkImportCompany>, update_contacts: Array<BulkImportContact> }>(
                    (acc, selectedRecord) => {
                        const { record, type, isImported } = selectedRecord;

                        // skip putting values into result, if user selected the dbRecord
                        if (!isImported) return acc;

                        if (type === CRM_ENTITY_TYPE.COMPANY) acc.update_companies.push(record);
                        else acc.update_contacts.push(record);
                        return acc;
                    },
                    {
                        // put duplicate values if any, as initial value to update-* arrays
                        update_companies: [...prev.data.update_companies.duplicates],
                        update_contacts: [...prev.data.update_contacts.duplicates],
                    }
                );

            return {
                type: SCREEN_TYPE.SUMMARY,
                // The only diff with prev.data is to pass the array of update_companies and contacts instead of conflicts {}
                data: { ...prev.data, ...updatedItemsObj }
            }
        });
    };

    const modalHeader = <ModalHeader title="Validate Import" onClose={onClose} />
    const modalFooter = (
        <ModalFooter
            disabled={isConflictScreen}
            onAccept={onConfirmImport}
            onReject={onClose}
            continueButtonLabel="Confirm Import"
            isLoading={isUploading}
        />
    );

    const conflictsArr = useMemo(() => {
        if (!isConflictScreen) return [];
        const { update_companies, update_contacts } = screenData.data;

        const result: Array<ConflictPair> = [];

        if (update_companies.companies && update_companies.conflicts) {
            const dbCompaniesMap = getValueMapById(update_companies.conflicts);

            // basically map over every conflicted imported company record
            // and find it's corresponding db record to show in Conflict UI
            update_companies.companies.forEach((importedCompany) => {
                const dbRecord = dbCompaniesMap.get(importedCompany.id);
                // The dbRecord will always exist for conflicted imported companies,
                // but TS doesn't know it, so added this check.
                if (!dbRecord) return;
                result.push({ type: CRM_ENTITY_TYPE.COMPANY, dbRecord, importedRecord: importedCompany });
            });
        }

        // same for contacts
        if (update_contacts.contacts && update_contacts.conflicts) {
            const dbContactsMap = getValueMapById(update_contacts.conflicts);

            update_contacts.contacts.forEach((importedContact) => {
                const dbRecord = dbContactsMap.get(importedContact.id);
                if (!dbRecord) return;
                result.push({ type: CRM_ENTITY_TYPE.CONTACT, dbRecord, importedRecord: importedContact });
            });
        }

        return result;
    }, [isConflictScreen, screenData.data]);

    return (
        <Modal open header={modalHeader} footer={modalFooter} borderRadius="6px">
            {isConflictScreen
                ? (
                    <ResolveConflictsContent
                        handleNextStep={handleNextStepAfterConflictResolve}
                        conflicts={conflictsArr}
                    />
                ) : (
                    <ImportSummaryContent
                        data={screenData.data}
                        errorMessage={axiosError?.status === 400
                            ? axiosError?.response?.data.message : undefined
                        }
                    />
                )
            }
        </Modal>
    );
});

export default ResolutionModal;